import { ChipGroup, type Chip } from "@bfc-software/pallet";

/**
 * Re-exported rather than redeclared: the chip shape is pallet's now
 * (SR-301 phase 6), and the two call sites here name the type when they build
 * their chip arrays.
 */
export type ScopeChip<Key extends string> = Chip<Key>;

/**
 * The status filter above the table.
 *
 * Thin wrapper over pallet's `ChipGroup` -- all it adds is `overrideNote`,
 * which `ChipGroup` deliberately does not carry: a caption explaining that a
 * search has widened the view past the chips is this screen's own editorial,
 * not chip-row furniture.
 *
 * `overrideNote`, when present, replaces the row's authority: a search widens
 * the view to every status, and saying so is what stops the chips reading as a
 * lie.
 */
// TODO: pallet's `ToggleChip` (SR-301 phase 4) now owns the same pill
// geometry - 20px radius, 13px/600 text, a fixed leading slot - from a token
// set in `toggle-chip.module.css`, and `ChipGroup` (SR-301 phase 6) keeps its
// own copy. These are deliberately different components (`ChipGroup` is
// single-select on a `RadioGroup`; `ToggleChip` is multi-select on a
// `ToggleButton`), so this is not a merge - but the shared *geometry*
// plausibly wants to be one pill token both consume. Carried forward from
// phase 4; out of scope for phase 6.
export function ScopeChips<Key extends string>({
  chips,
  selected,
  onSelect,
  ariaLabel,
  overrideNote,
}: {
  chips: ReadonlyArray<ScopeChip<Key>>;
  selected: Key;
  onSelect: (key: Key) => void;
  ariaLabel: string;
  overrideNote?: string;
}) {
  return (
    <div className="flex flex-wrap items-center">
      <ChipGroup
        data-test-role="scope-chips"
        ariaLabel={ariaLabel}
        chips={chips.map((chip) => ({
          ...chip,
          "data-test-role": "scope-chip",
          "data-scope": chip.key,
        }))}
        selected={selected}
        onSelect={onSelect}
      />
      {overrideNote ? (
        <span
          className="ml-8 text-on-surface-variant text-size-12"
          data-test-role="scope-override"
        >
          {overrideNote}
        </span>
      ) : (
        <></>
      )}
    </div>
  );
}
